import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  useTheme,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import { baseUrl } from '../../api/baseQuery';
import { getAuthHeaders } from '../../utils/authHeaders';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { reportError } from '../../utils/reportError';
import ContentUnavailable from '../../components/ContentUnavailable';
import KeycloakContext from '../../KeycloakProvider';
import * as XLSX from 'xlsx';

const ReportsList = ({ endpoint, title, allColumns = false, initialData = null, hideMainTitle = false, enableFilters = true, disableReportTypeFilter = false, filenamePrefix = null, filenameFilter = null }) => {
  const theme = useTheme();
  const { currentUser } = useCurrentUser();
  const { keycloak } = useContext(KeycloakContext);
  
  // Get user's keycloak groups
  const userGroups = keycloak?.tokenParsed?.realm_access?.roles || [];
  
  // Determine if user is T-Mobile, Pierson, or AT&T
  const userCustomerGroup = 
    userGroups.includes('T-Mobile') ? 'T-Mobile' :
    userGroups.includes('Pierson') ? 'Pierson' :
    userGroups.includes('AT&T') ? 'AT&T' :
    null;
  
  // Determine if user is internal (can see all reports)
  const internalGroups = ['admin', 'NOC', 'Manager', 'VP', 'Executive', 'Construction', 'Dashboard', 'C-Suite'];
  const isInternalUser = userGroups.some(role => internalGroups.includes(role));
  
  // Function to filter files by customer name in filename
  const filterByCustomerName = (files) => {
    // If user is internal or not from T-Mobile/Pierson/AT&T, show all files
    if (isInternalUser || !userCustomerGroup) {
      return files;
    }
    
    // Filter to only show files with customer name in filename (case-insensitive)
    return files.filter(file => {
      const filename = file.filename.toLowerCase();
      const customerName = userCustomerGroup.toLowerCase();
      return filename.includes(customerName);
    });
  };
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedFileKind, setSelectedFileKind] = useState('pdf');
  const [selectedSpreadsheetImageUrl, setSelectedSpreadsheetImageUrl] = useState(null);
  const [comments, setComments] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [reportComments, setReportComments] = useState({});
  const [editingFilename, setEditingFilename] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [isSavingTableComment, setIsSavingTableComment] = useState(false);
  
  // Filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedReportType, setSelectedReportType] = useState('');
  const [reportTypes, setReportTypes] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);

  useEffect(() => {
    // If initialData is provided, use it directly
    if (initialData && initialData.files) {
      const allFiles = initialData.files;
      let files = filenameFilter
        ? allFiles.filter(f => new RegExp(filenameFilter, 'i').test(f.filename))
        : allFiles;
      // Apply customer name filtering
      files = filterByCustomerName(files);
      setReports(files);
      setReportTypes(extractUniqueReportTypes(files));
      if (files.length > 0) {
        fetchAllComments(files);
      } else {
        reportError({ page: title || endpoint, detail: `${endpoint} returned empty files array` });
      }
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders({
          Accept: 'application/json',
        });
        const response = await fetch(`${baseUrl}/${endpoint}`, {
          headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const allFiles = data.files || [];
        let files = filenameFilter
          ? allFiles.filter(f => new RegExp(filenameFilter, 'i').test(f.filename))
          : allFiles;
        // Apply customer name filtering
        files = filterByCustomerName(files);
        setReports(files);
        setReportTypes(extractUniqueReportTypes(files));
        setError(null);

        if (files.length === 0) {
          reportError({ page: title || endpoint, detail: `${endpoint} returned empty files array` });
        }

        // Fetch comments for all reports
        if (files.length > 0) {
          await fetchAllComments(files);
        }
      } catch (err) {
        setError('Failed to load reports: ' + err.message);
        console.error('Error fetching reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterByCustomerName/extractUniqueReportTypes are recreated every render; including them would cause a refetch loop
  }, [endpoint, initialData]);



  // Helper function to extract date from filename
  const extractDateFromFilename = (filename) => {
    // Try to match YYYY-MM-DD pattern
    const dateMatch = filename.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1];
    }
    // Try to match DDMMYYYY or YYYYMMDD patterns
    const altDateMatch = filename.match(/(\d{8})/);
    if (altDateMatch) {
      const dateStr = altDateMatch[1];
      // Try to parse as YYYYMMDD
      if (/^\d{8}$/.test(dateStr)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
    }
    return null;
  };

  // Helper function to extract report type from filename
  const extractReportTypeFromFilename = (filename) => {
    // Common report type patterns
    const typePatterns = [
      { pattern: /SU/i, type: 'SU' },
      { pattern: /Caesar/i, type: 'Caesars' },
      { pattern: /Intl/i, type: 'International' },
      { pattern: /Windsor/i, type: 'Windsor' },
      { pattern: /T-?Mobile|TMobile/i, type: 'T-Mobile' },
      { pattern: /AT&T|ATT/i, type: 'AT&T' },
    ];

    for (const { pattern, type } of typePatterns) {
      if (pattern.test(filename)) {
        return type;
      }
    }
    
    return null;
  };

  // Extract unique report types from all reports
  const extractUniqueReportTypes = (reportsList) => {
    const types = new Set();
    reportsList.forEach(report => {
      const type = extractReportTypeFromFilename(report.filename);
      if (type) {
        types.add(type);
      }
    });
    return Array.from(types).sort();
  };

  // Apply filters to reports
  const applyFilters = (reportsList) => {
    return reportsList.filter(report => {
      // Date filtering
      if (dateFrom || dateTo) {
        const reportDate = extractDateFromFilename(report.filename);
        if (reportDate) {
          if (dateFrom && reportDate < dateFrom) return false;
          if (dateTo && reportDate > dateTo) return false;
        }
      }

      // Report type filtering
      if (selectedReportType) {
        const reportType = extractReportTypeFromFilename(report.filename);
        if (reportType !== selectedReportType) return false;
      }

      return true;
    });
  };

  // Re-apply filters whenever reports, dateFrom, dateTo, or selectedReportType changes
  useEffect(() => {
    const filtered = applyFilters(reports);
    setFilteredReports(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyFilters is recreated every render; including it would cause an infinite filter loop
  }, [reports, dateFrom, dateTo, selectedReportType]);

  const fetchAllComments = async (files) => {
    try {
      const commentsMap = {};
      
      // Fetch comment for each report
      for (const file of files) {
        try {
          const headers = await getAuthHeaders({
            Accept: 'application/json',
          });
          const response = await fetch(`${baseUrl}/getReportComments/?filename=${encodeURIComponent(file.filename)}`, {
            headers,
          });

          if (response.ok) {
            const data = await response.json();
            commentsMap[file.filename] = data.comment || '';
          }
        } catch (err) {
          console.error(`Error fetching comment for ${file.filename}:`, err);
        }
      }
      
      setReportComments(commentsMap);
    } catch (err) {
      console.error('Error fetching all comments:', err);
    }
  };

  const getViewApiUrl = (filename) => {
    // filenames from listPdfFolder already include the folder path (e.g. "su/Weekly-2025-10-01.pdf")
    if (filenamePrefix) {
      return `${baseUrl}/download-pdf/?filename=${encodeURIComponent(filename)}`;
    }
    return `${baseUrl}/downloadPdf/?filename=${encodeURIComponent(filename)}`;
  };

  const getDownloadApiCandidates = (filename) => {
    const encodedFilename = encodeURIComponent(filename);
    const cleanFilename = (filename || '').split('?')[0].toLowerCase();
    const isExcelLike = /\.(xlsx|xls|csv)$/.test(cleanFilename);

    if (isExcelLike) {
      return [
        `${baseUrl}/downloadExcel/?filename=${encodedFilename}`,
        `${baseUrl}/download-excel/?filename=${encodedFilename}`,
      ];
    }

    if (filenamePrefix) {
      return [
        `${baseUrl}/downloadPdf/?filename=${encodedFilename}`,
        `${baseUrl}/download-pdf/?filename=${encodedFilename}`,
      ];
    }
    return [
      `${baseUrl}/downloadPdf/?filename=${encodedFilename}`,
      `${baseUrl}/download-pdf/?filename=${encodedFilename}`,
    ];
  };

  const getFileExtension = (filename) => {
    const cleanName = (filename || '').split('?')[0];
    const dotIndex = cleanName.lastIndexOf('.');
    return dotIndex >= 0 ? cleanName.substring(dotIndex + 1).toLowerCase() : '';
  };

  const getBaseFilename = (filename) => {
    if (!filename) return 'download';
    const normalized = filename.replace(/\\/g, '/');
    return normalized.split('/').pop() || 'download';
  };

  const parseContentDispositionFilename = (contentDisposition) => {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
      } catch (err) {
        return utf8Match[1].replace(/"/g, '');
      }
    }

    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (basicMatch?.[1]) {
      return basicMatch[1];
    }

    return null;
  };

  const normalizeDownloadFilename = (requestedFilename, serverFilename) => {
    const requestedBase = getBaseFilename(requestedFilename);
    const serverBase = serverFilename ? getBaseFilename(serverFilename) : '';
    const requestedExt = getFileExtension(requestedBase);
    const serverExt = getFileExtension(serverBase);

    if (!serverBase) return requestedBase;
    if (!requestedExt || requestedExt === serverExt) return serverBase;

    const serverWithoutExt = serverBase.replace(/\.[^.]+$/, '');
    return `${serverWithoutExt}.${requestedExt}`;
  };

  const isPdfFile = (filename) => getFileExtension(filename) === 'pdf';

  const isSpreadsheetFile = (filename) => {
    const extension = getFileExtension(filename);
    return ['xlsx', 'xls', 'csv'].includes(extension);
  };

  const isPdfBlob = async (blob) => {
    try {
      const header = await blob.slice(0, 5).text();
      return header === '%PDF-';
    } catch (err) {
      return false;
    }
  };

  const toExcelColumnLabel = (index) => {
    let value = index + 1;
    let result = '';
    while (value > 0) {
      const rem = (value - 1) % 26;
      result = String.fromCharCode(65 + rem) + result;
      value = Math.floor((value - 1) / 26);
    }
    return result;
  };

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const convertSpreadsheetBlobToImageUrl = async (blob) => {
    const MAX_PREVIEW_ROWS = 120;
    const MAX_PREVIEW_COLS = 18;
    const ROW_HEIGHT = 24;
    const HEADER_HEIGHT = 28;
    const ROW_INDEX_WIDTH = 46;
    const CELL_H_PADDING = 8;

    const arrayBuffer = await blob.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0] || 'Sheet1';
    const worksheet = workbook.Sheets[firstSheetName];
    const grid = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: '' }) || [];

    const visibleRows = grid.slice(0, MAX_PREVIEW_ROWS);
    const detectedCols = Math.max(1, ...visibleRows.map(row => row.length || 0));
    const colCount = Math.min(detectedCols, MAX_PREVIEW_COLS);

    const colWidths = Array.from({ length: colCount }, (_, colIndex) => {
      const maxChars = Math.max(
        toExcelColumnLabel(colIndex).length,
        ...visibleRows.map(row => `${row[colIndex] ?? ''}`.length)
      );
      return clamp(20 + maxChars * 6, 90, 260);
    });

    const gridWidth = colWidths.reduce((sum, width) => sum + width, 0);
    const totalWidth = ROW_INDEX_WIDTH + gridWidth + 1;
    const totalHeight = HEADER_HEIGHT + visibleRows.length * ROW_HEIGHT + 1;

    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = Math.max(totalHeight, HEADER_HEIGHT + ROW_HEIGHT + 1);

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to initialize spreadsheet preview');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Excel-like headers (column letters + row numbers)
    ctx.fillStyle = '#f3f3f3';
    ctx.fillRect(0, 0, canvas.width, HEADER_HEIGHT);
    ctx.fillRect(0, 0, ROW_INDEX_WIDTH, canvas.height);

    ctx.strokeStyle = '#d0d7de';
    ctx.lineWidth = 1;
    ctx.beginPath();

    let cursorX = ROW_INDEX_WIDTH;
    for (let col = 0; col < colCount; col += 1) {
      ctx.moveTo(cursorX + 0.5, 0);
      ctx.lineTo(cursorX + 0.5, canvas.height);
      cursorX += colWidths[col];
    }
    ctx.moveTo(ROW_INDEX_WIDTH + gridWidth + 0.5, 0);
    ctx.lineTo(ROW_INDEX_WIDTH + gridWidth + 0.5, canvas.height);

    for (let row = 0; row <= visibleRows.length; row += 1) {
      const y = HEADER_HEIGHT + row * ROW_HEIGHT;
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(canvas.width, y + 0.5);
    }
    ctx.moveTo(ROW_INDEX_WIDTH + 0.5, 0);
    ctx.lineTo(ROW_INDEX_WIDTH + 0.5, canvas.height);
    ctx.stroke();

    ctx.fillStyle = '#1f2328';
    ctx.font = '12px Calibri, Arial, sans-serif';
    ctx.textBaseline = 'middle';

    // Column labels
    let labelX = ROW_INDEX_WIDTH;
    for (let col = 0; col < colCount; col += 1) {
      const label = toExcelColumnLabel(col);
      const width = colWidths[col];
      const textWidth = ctx.measureText(label).width;
      ctx.fillText(label, labelX + (width - textWidth) / 2, HEADER_HEIGHT / 2);
      labelX += width;
    }

    // Row labels + cell text
    for (let rowIndex = 0; rowIndex < visibleRows.length; rowIndex += 1) {
      const yCenter = HEADER_HEIGHT + rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
      const rowLabel = `${rowIndex + 1}`;
      const rowLabelWidth = ctx.measureText(rowLabel).width;
      ctx.fillText(rowLabel, ROW_INDEX_WIDTH - 8 - rowLabelWidth, yCenter);

      let cellX = ROW_INDEX_WIDTH;
      for (let colIndex = 0; colIndex < colCount; colIndex += 1) {
        const cellWidth = colWidths[colIndex];
        const cellText = `${visibleRows[rowIndex]?.[colIndex] ?? ''}`;

        ctx.save();
        ctx.beginPath();
        ctx.rect(cellX + 1, HEADER_HEIGHT + rowIndex * ROW_HEIGHT + 1, cellWidth - 2, ROW_HEIGHT - 2);
        ctx.clip();
        ctx.fillText(cellText, cellX + CELL_H_PADDING, yCenter);
        ctx.restore();

        cellX += cellWidth;
      }
    }

    const imageBlob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/png');
    });
    if (!imageBlob) throw new Error('Failed to render spreadsheet preview image');

    return URL.createObjectURL(imageBlob);
  };

  const handleViewPdf = async (filename) => {
    try {
      setPdfLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch(getViewApiUrl(filename), {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      setSelectedPdf(filename);

      if (isPdfFile(filename)) {
        const blobUrl = URL.createObjectURL(blob);
        setSelectedPdfUrl(blobUrl);
        setSelectedSpreadsheetImageUrl(null);
        setSelectedFileKind('pdf');
      } else if (isSpreadsheetFile(filename)) {
        const screenshotLikeUrl = await convertSpreadsheetBlobToImageUrl(blob);
        setSelectedPdfUrl(null);
        setSelectedSpreadsheetImageUrl(screenshotLikeUrl);
        setSelectedFileKind('spreadsheet-image');
      } else {
        setSelectedPdfUrl(null);
        setSelectedSpreadsheetImageUrl(null);
        setSelectedFileKind('unsupported');
      }

      setOpenPdfDialog(true);
      
      // Fetch comments for this report
      await fetchComments(filename);
    } catch (err) {
      console.error('Error viewing PDF:', err);
      alert('Failed to view file: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfDialog = () => {
    setOpenPdfDialog(false);
    if (selectedPdfUrl) {
      URL.revokeObjectURL(selectedPdfUrl);
    }
    if (selectedSpreadsheetImageUrl) {
      URL.revokeObjectURL(selectedSpreadsheetImageUrl);
    }
    setSelectedFileKind('pdf');
    setSelectedPdf(null);
    setSelectedPdfUrl(null);
    setSelectedSpreadsheetImageUrl(null);
  };

  const handleDownloadPdf = async (filename) => {
    try {
      const headers = await getAuthHeaders();
      const candidates = getDownloadApiCandidates(filename);
      const expectsSpreadsheet = isSpreadsheetFile(filename);

      let selectedBlob = null;
      let selectedServerFilename = null;
      let lastStatus = null;

      for (const apiUrl of candidates) {
        const response = await fetch(apiUrl, { headers });
        if (!response.ok) {
          lastStatus = response.status;
          continue;
        }

        const blob = await response.blob();
        const blobLooksPdf = await isPdfBlob(blob);
        if (expectsSpreadsheet && blobLooksPdf) {
          // Skip endpoints that convert xlsx/xls/csv to PDF.
          continue;
        }

        selectedBlob = blob;
        selectedServerFilename = parseContentDispositionFilename(response.headers.get('content-disposition'));
        break;
      }

      if (!selectedBlob) {
        throw new Error(`Failed to download file${lastStatus ? `: ${lastStatus}` : ''}`);
      }

      const downloadName = normalizeDownloadFilename(filename, selectedServerFilename);
      const blobUrl = URL.createObjectURL(selectedBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download file: ' + err.message);
    }
  };

  const fetchComments = async (filename) => {
    try {
      setCommentsLoading(true);
      const headers = await getAuthHeaders({
        Accept: 'application/json',
      });
      const response = await fetch(`${baseUrl}/getReportComments/?filename=${encodeURIComponent(filename)}`, {
        headers,
      });

      if (!response.ok) {
        console.error('Error fetching comments:', response.status);
        setComments('');
        return;
      }

      const data = await response.json();
      setComments(data.comment || '');
      setNewComment(data.comment || '');
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments('');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSaveComment = async (filename) => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      setIsSavingComment(true);
      const headers = await getAuthHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      });
      const response = await fetch(`${baseUrl}/updateReportComment/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          filename: filename,
          comment: newComment
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save comment: ${response.status}`);
      }

      setComments(newComment);
      alert('Comment saved successfully!');
    } catch (err) {
      console.error('Error saving comment:', err);
      alert('Failed to save comment: ' + err.message);
    } finally {
      setIsSavingComment(false);
    }
  };

  const handleEditTableComment = (filename) => {
    setEditingFilename(filename);
    setEditComment(reportComments[filename] || '');
  };

  const handleSaveTableComment = async () => {
    if (!editingFilename) return;

    try {
      setIsSavingTableComment(true);
      const headers = await getAuthHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      });
      const response = await fetch(`${baseUrl}/updateReportComment/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          filename: editingFilename,
          comment: editComment
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save comment: ${response.status}`);
      }

      // Update the reportComments state
      setReportComments(prev => ({
        ...prev,
        [editingFilename]: editComment
      }));
      setEditingFilename(null);
      setEditComment('');
      alert('Comment saved successfully!');
    } catch (err) {
      console.error('Error saving comment:', err);
      alert('Failed to save comment: ' + err.message);
    } finally {
      setIsSavingTableComment(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // If customer user has no matching files, hide this page completely
  if (!loading && reports.length === 0 && userCustomerGroup) {
    return null;
  }

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedReportType('');
  };

  const hasActiveFilters = dateFrom || dateTo || selectedReportType;

  return (
    <Container maxWidth="lg" sx={hideMainTitle ? { py: 0, px: '0 !important', maxWidth: '100% !important' } : { py: 4 }}>
      {!hideMainTitle && (
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          {title}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && reports.length === 0 ? (
        <ContentUnavailable page={title || endpoint} />
      ) : (
        <>
          {/* Filters Section */}
          {enableFilters && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                {reportTypes.length > 0 && !disableReportTypeFilter && (
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Report Type</InputLabel>
                      <Select
                        value={selectedReportType}
                        onChange={(e) => setSelectedReportType(e.target.value)}
                        label="Tipo di Report"
                      >
                        {reportTypes.map(type => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {hasActiveFilters && (
                  <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                    >
                      Clear filters
                    </Button>
                  </Grid>
                )}
              </Grid>
              {filteredReports.length < reports.length && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`${filteredReports.length} / ${reports.length} reports found`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Paper>
          )}

          {/* Reports Table */}
        <Box sx={{ width: '100%' }}>
          {title && (
            <Typography
              sx={{ fontWeight: '700', fontSize: '20px', mb: 1, mx: 0.5 }}
            >
              {title}
            </Typography>
          )}
          <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
            <Table>
              <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Filename</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '300px' }}>Comments</TableCell>
                  {allColumns && <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Comment</TableCell>}
                  {allColumns && <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Approved</TableCell>}
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allColumns ? 6 : 4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No reports found with selected filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report, index) => (
                  <TableRow 
                    key={index} 
                    hover
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                      '&:hover': {
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#eeeeee',
                      }
                    }}
                  >
                  <TableCell>{report.filename.replace(/^[^/]+\//, '')}</TableCell>
                  <TableCell>
                    {currentUser && (currentUser.groups?.includes('NOC') || currentUser.groups?.includes('admin')) ? (
                      <Box
                        onClick={() => handleEditTableComment(report.filename)}
                        sx={{
                          p: 1,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: '1px solid #e0e0e0',
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: '#eeeeee',
                            border: `1px solid ${theme.palette.primary.main}`
                          }
                        }}
                      >
                        {reportComments[report.filename] ? (
                          <span>{reportComments[report.filename]}</span>
                        ) : (
                          <Typography variant="caption" color="textSecondary">Click to add comment...</Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {reportComments[report.filename] || <span style={{ color: '#999' }}>No comment</span>}
                      </Typography>
                    )}
                  </TableCell>
                  {allColumns && <TableCell>{report.comment || '-'}</TableCell>}
                  {allColumns && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: report.approved ? 'green' : 'orange',
                          fontWeight: 'bold',
                        }}
                      >
                        {report.approved ? 'Approved' : 'Pending'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewPdf(report.filename)}
                        title="View file"
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<GetAppIcon />}
                        onClick={() => handleDownloadPdf(report.filename)}
                        title="Download file"
                      >
                        Download
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
        </>
      )}

      {/* PDF Viewer Dialog */}
      <Dialog
        open={openPdfDialog}
        onClose={handleClosePdfDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{selectedPdf?.replace(/^[^/]+\//, '')}</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={() => handleDownloadPdf(selectedPdf)}
            disabled={pdfLoading}
          >
            Download
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
          {pdfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : selectedPdf ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', overflow: 'hidden' }}>
              {selectedFileKind === 'pdf' && selectedPdfUrl ? (
                <Box
                  component="iframe"
                  src={selectedPdfUrl}
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    border: 'none',
                  }}
                />
              ) : selectedFileKind === 'spreadsheet-image' && selectedSpreadsheetImageUrl ? (
                <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto', backgroundColor: '#eceff3', p: 2 }}>
                  <Box
                    component="img"
                    src={selectedSpreadsheetImageUrl}
                    alt="Spreadsheet preview"
                    sx={{
                      display: 'block',
                      maxWidth: 'none',
                      border: '1px solid #cfd8dc',
                      boxShadow: '0 4px 18px rgba(0,0,0,0.12)',
                      backgroundColor: '#ffffff',
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ width: '100%', flex: 1, minHeight: 0, p: 2 }}>
                  <Alert severity="warning">
                    Preview is not available for this file type. Please use Download.
                  </Alert>
                </Box>
              )}
              <Divider sx={{ flexShrink: 0 }} />
              
              {/* Comments Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, flexShrink: 0, maxHeight: '200px', overflow: 'auto', backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Comment</Typography>
                
                {/* Display current comment */}
                {commentsLoading ? (
                  <CircularProgress size={20} />
                ) : comments ? (
                  <Paper sx={{ p: 1, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{comments}</Typography>
                  </Paper>
                ) : (
                  <Typography variant="caption" color="textSecondary">No comment yet</Typography>
                )}

                {/* Edit Comment - Only for NOC and Admin users */}
                {currentUser && (currentUser.groups?.includes('NOC') || currentUser.groups?.includes('admin')) ? (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Add or edit comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={isSavingComment}
                      multiline
                      maxRows={2}
                      sx={{ flex: 1, minWidth: '200px' }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleSaveComment(selectedPdf)}
                      disabled={isSavingComment || newComment === comments}
                      sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
                    >
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Only NOC and Admin users can edit comments
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Comment Edit Dialog */}
      <Dialog
        open={!!editingFilename}
        onClose={() => setEditingFilename(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Comment for {editingFilename}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Add or edit comment..."
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
            disabled={isSavingTableComment}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFilename(null)} disabled={isSavingTableComment}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTableComment}
            disabled={isSavingTableComment}
          >
            {isSavingTableComment ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsList;
